/**
 * A booking's page (`/account/bookings/:bookingId`, web-account-pages D4,
 * signed-in-web.html): the breadcrumb, the session's heading with when and its
 * status, the app's notice for the booking's state, the booked page's ticket
 * (When with Add to Calendar, Duration, Location with Get directions or Join,
 * and the app's payment row with any refund), How to prepare, About, Message
 * the studio, and the actions the app allows: cancel within the studio's rule
 * (the cancel window), withdraw a request, Pay now, Book again.
 */
import type { SitePhoto } from '@telify/shared';

import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'booking-page',
  description:
    "One of the client's bookings: when, how long and where, the payment, How to prepare, Message the studio, and cancel, Complete booking, Pay now or Book again as the app allows.",
  options: {},
  parts: {
    page: { description: 'The whole page.', required: false },
    crumbs: { description: 'Bookings / the session: back to the Bookings page.', required: false },
    heading: { description: "The session's name, who with, when and its status.", required: false },
    row: { description: "One row of the booking's ticket: its label and words.", required: false },
    amount: { description: "A ticket row's amount, at its end.", required: false },
    notice: { description: "The app's line for the booking's state (booked for you with its deadline, request submitted, approved, waitlisted).", required: false },
    ticket: { description: 'When, Duration, Location and Payment.', required: true },
    addToCalendar: { description: 'Add to Calendar, right of When.', required: false },
    directions: { description: 'Get directions, right of the place.', required: false },
    join: { description: 'Join, right of an online place: opens the meeting link.', required: false },
    passcode: { description: "The meeting's passcode: copies it.", required: false },
    refund: { description: 'Each refund and the net paid, under the payment.', required: true },
    preparation: { description: "How to prepare: the studio's note for the session.", required: false },
    about: { description: 'About this session or event.', required: false },
    messageStudio: { description: 'Message the studio: opens the messages.', required: false, role: 'secondary-action' },
    cancel: { description: 'Cancel this booking, Cancel this request or Leave the waitlist: opens the cancel window.', required: true },
    cancelClosed: { description: 'Why online cancellation is closed, and how to reach the studio.', required: true },
    cancelWindow: { description: "The cancel window: what is cancelled, the studio's refund rule, Keep and Yes.", required: true },
    complete: { description: 'Complete booking (or Register): opens the Complete booking step of a booking the studio made that still owes a form.', required: true, role: 'primary-action' },
    payNow: { description: 'Pay now: an approved request awaiting card payment.', required: true, role: 'primary-action' },
    bookAgain: { description: "Book again: the service's page, for a past or cancelled booking.", required: false, role: 'primary-action' },
    telegramNote: { description: 'Changes and reminders come to the Telegram chat.', required: false },
    done: { description: 'The note after a cancel.', required: false },
    readError: { description: 'The page when the booking could not be read: what went wrong and Try again.', required: true },
  },
  actions: {
    openCancel: { description: 'Opens the cancel window and reads the refund rule that applies now.', args: [] },
    closeCancel: { description: 'Keeps the booking: closes the cancel window.', args: [] },
    confirmCancel: { description: 'Cancels the booking (or withdraws the request).', args: [] },
    addToCalendar: { description: 'Downloads the booking as a calendar file.', args: [] },
    copyPasscode: { description: "Copies the meeting's passcode.", args: [] },
    messageStudio: { description: 'Opens the messages panel.', args: [] },
    open: { description: 'Opens a page of the site (Bookings, Pay now, Book again).', args: [{ name: 'href', kind: 'value', type: 'string', format: 'path' }] },
    retry: { description: 'Reads the booking again after a failed read.', args: [] },
  },
  events: {
    cancelled: 'The booking was cancelled or the request withdrawn here.',
  },
});

/** One row of the booking's ticket. */
export interface BookingPageRow {
  /** When, Requested, Was scheduled, Duration, Location, Payment. */
  readonly label: string;
  /** The row's words. */
  readonly value: string;
  /** The smaller line under them, or null. */
  readonly sub: string | null;
  /** The place's note under them, or null. */
  readonly note: string | null;
  /** The amount on the right, or null. */
  readonly amount: string | null;
  /** On an in-person Location: the directions link; null otherwise. */
  readonly directionsHref: string | null;
}

/** One refund under the payment. */
export interface BookingPageRefund {
  /** Refunded, or On its way. */
  readonly state: string;
  /** "Oct 12 · to Visa •••• 4242". */
  readonly when: string;
  /** "−$125.00". */
  readonly amount: string;
}

/** The cancel window. */
export interface BookingPageCancelWindow {
  /** Cancel booking, Cancel request, Leave waitlist. */
  readonly kicker: string;
  /** "Cancel this booking?" */
  readonly title: string;
  /** The session's day, said in bold. */
  readonly date: string;
  /** What follows the day: " session with Noor Hadley will be cancelled. This can't be undone." */
  readonly rest: string;
  /** "Need a different time instead?" for a session; null for an event. */
  readonly nudge: string | null;
  /** The refund rule that applies now, the check in progress, or that it could not be read; null when nothing was paid. */
  readonly policy: { readonly state: 'checking' | 'ready' | 'unavailable'; readonly text: string } | null;
  /** Keep my booking. */
  readonly keepLabel: string;
  /** Yes, cancel booking (Cancelling… while it goes). */
  readonly yesLabel: string;
  /** True while the cancel is on its way. */
  readonly busy: boolean;
}

/** What a booking's page knows. */
export interface BookingPageState {
  /** loading until the booking is read; not-found when it is not this client's. */
  // Grown (shared-client-screens B2b): `error`, the read failed for another reason; `readError` has its words.
  readonly status: 'loading' | 'ready' | 'not-found' | 'error';
  /** When the read failed for another reason than not-found: what the page says, with Try again; null otherwise. */
  readonly readError: { readonly title: string; readonly text: string; readonly retryLabel: string } | null;
  /** Your session, Your event, Your request. */
  readonly eyebrow: string;
  /** The service's name. */
  readonly name: string;
  /** The service's photo, or null. */
  readonly photoUrl: string | null;
  /** website-photo-zoom: the same photo framed as the studio set it (crop point and zoom), or null. */
  readonly photo: SitePhoto | null;
  /** The provider's name, or null. */
  readonly withName: string | null;
  /** The provider's photo, or null. */
  readonly withPhotoUrl: string | null;
  /** "Today at 2:00 PM", "In 5 days", "Was scheduled for Friday, October 16". */
  readonly when: string;
  /** Confirmed, Payment due, Pending approval, Completed, Cancelled. */
  readonly statusLabel: string;
  /** live (accent), done (muted), off (cancelled). */
  readonly tone: 'live' | 'done' | 'off';
  /** The app's line for the booking's state, or null. */
  readonly notice: { readonly icon: 'clock' | 'info'; readonly title: string; readonly text: string } | null;
  /** The ticket's rows. */
  readonly rows: readonly BookingPageRow[];
  /** Add to Calendar's Google Calendar and Outlook links, or null when there is nothing to add. */
  readonly calendar: { readonly google: string; readonly outlook: string } | null;
  /** Join's meeting link and its passcode, for a confirmed online booking; null otherwise. */
  readonly join: { readonly href: string | null; readonly passcode: string | null; readonly platform: string | null } | null;
  /** Each refund under the payment, and the net paid; null when nothing was refunded. */
  readonly refunds: { readonly lines: readonly BookingPageRefund[]; readonly net: string | null } | null;
  /** How to prepare: its heading and the studio's text (formatted), or null. */
  readonly preparation: { readonly heading: string; readonly html: string } | null;
  /** About this session or event (formatted), or null. */
  readonly about: { readonly heading: string; readonly html: string } | null;
  /** Message the studio: its heading and line. */
  readonly chat: { readonly heading: string; readonly line: string; readonly logoUrl: string | null };
  /** The cancel link's words, or null when the booking cannot be cancelled online. */
  readonly cancelLabel: string | null;
  /** Why online cancellation is closed and how to reach the studio, or null. */
  readonly cancelClosed: string | null;
  /** The cancel window while open, or null. */
  readonly cancelWindow: BookingPageCancelWindow | null;
  /** Why the cancel did not go through, or null. */
  readonly problem: string | null;
  /** The note after a cancel ("Booking canceled"), or null. */
  readonly done: string | null;
  /** The side's action: Pay now for an approved request still to pay, Book again for a past or cancelled one; null otherwise. */
  // Grown (web-account-pages D11): `complete`, Complete booking or Register for a booking the studio made that still owes a form.
  readonly errand: { readonly kind: 'complete' | 'payNow' | 'bookAgain'; readonly label: string; readonly href: string } | null;
  /** The Telegram line for a booking still ahead, or null. */
  readonly telegramNote: string | null;
  /** The studio's name. */
  readonly studioName: string;
  /** The Bookings page. */
  readonly bookingsHref: string;
}

/** What a drawing can ask a booking's page to do. */
export interface BookingPageActions {
  /** Open the cancel window. */
  readonly openCancel: () => void;
  /** Keep the booking: close the cancel window. */
  readonly closeCancel: () => void;
  /** Cancel the booking or withdraw the request. */
  readonly confirmCancel: () => void;
  /** Download the booking as a calendar file. */
  readonly addToCalendar: () => void;
  /** Copy the meeting's passcode. */
  readonly copyPasscode: () => void;
  /** Open the messages panel. */
  readonly messageStudio: () => void;
  /** Open a page of the site. */
  readonly open: (href: string) => void;
  /** Read the booking again after a failed read. */
  readonly retry: () => void;
}

/** What a booking's page tells its host. */
export interface BookingPageEvents {
  /** The booking was cancelled or the request withdrawn. */
  readonly cancelled: Record<string, never>;
}
